import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { getNotifMsg } from "utils/message";
import {
  updateAgentProfile,
  updateNotificationSounds,
  updateTelegramBot,
} from "redux/chats/chats.actions";
import { DEFAULT_AVATAR_URL, RAYCHAT_DASHBOARD_URL } from "utils/constants";
import SelectBox from "../../../SelectBox";
import { toast } from "react-toastify";
import { isValidEmail } from "utils/validation";
import "./styles.scss";
import useTranslation from "hooks/useTranslation";
import {
  isImageSizeValidForUpload,
  isValidFileToUploadAsImage,
} from "utils/validation";
import ImageInputWrapper from "components/Conversations/Settings/Profile/components/Wrapper/ImageInputWrapper";
import { ThreeDots } from "react-loader-spinner";
import {
  PowerButton,
  TelegramMiniIcon,
  UserInfoIcon,
  XIcon,
} from "assets/icons";
import TypicalHeader from "./components/Header/TypicalHeader";
import Notifications from "../Notifications";
import ThemeSettings from "../Theme";
import { ReactComponent as PurchaseIcon } from "assets/icons/purchase_bag.svg";

const getLocale = ({ settingsData }) => settingsData.locale;
const getAgent = ({ userData }) => userData.agent.data;
const getContainer = ({ userData }) => userData.container.data;

const VALID_INPUT_OBJ = {
  isValid: true,
  msg: "",
};

const Profile = ({
  handleCloseSettings,
  toggleSettings,
  setShowLogoutModal,
}) => {
  const container = useSelector(getContainer);
  const locale = useSelector(getLocale);
  const agent = useSelector(getAgent);
  const [formData, setFormData] = useState(null);

  const [editingAgent, setEditingAgent] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const dispatch = useDispatch();
  const { setAppLocale, getAppLocale, getTranslation } = useTranslation([
    "COMMON",
    "CHAT",
  ]);
  const { t } = getTranslation();

  const convsChargeCount = +container?.account?.conversationCharge;
  // packages with infinity convs count
  const isCountInfinity = convsChargeCount > 500000;

  const FIELD_REQUIRED = t("FORM_ERRORS").FIELD_REQUIRED;
  const INVALID_EMAIL = t("FORM_ERRORS").INVALID_EMAIL;
  const INVALID_PASSWORD = t("FORM_ERRORS").INVALID_PASSWORD;

  const APP_LOCALES = [
    {
      title: t("PROFILE").SETTINGS.FA_LANG,
      value: "fa",
    },
    {
      title: t("PROFILE").SETTINGS.EN_LANG,
      value: "en",
    },
  ];

  const handleChangeLocale = (value) => {
    setAppLocale(value);
    handleCloseSettings();
  };

  const handleUpdateEditingAgent = (name, value) => {
    setEditingAgent((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  /**
   *
   * @param {string} themeIndicator indicator: "1" for "light" & "2" for "dark" (numbers are stringified)
   */
  const handleThemeToggle = (themeIndicator) => {
    handleUpdateEditingAgent("appStyle", themeIndicator);
  };

  const handleChangeImageInput = (e) => {
    const file = e.target.files[0];

    // type validation
    if (!isValidFileToUploadAsImage(file?.type)) {
      toast.error(getNotifMsg("INVALID_FILE_FORMAT", locale));
      return;
    }

    // size validation
    if (!isImageSizeValidForUpload(file?.size)) {
      toast.error(getNotifMsg("Image_SIZE_EXCEEDS", locale));
      return;
    }

    setSelectedImage({ url: URL.createObjectURL(file), file });
  };

  const handleSendAgentInfo = () => {
    if (isLoadingSubmit) return;

    setIsLoadingSubmit(true);

    dispatch(updateNotificationSounds(formData));

    dispatch(
      updateAgentProfile(
        { agentInfo: { ...editingAgent }, newAvatar: selectedImage },
        setIsLoadingSubmit
      )
    );
  };

  const getInputStyle = (dir) => {
    const direction = dir === "left" ? "ltr" : "rtl";
    return {
      textAlign: dir,
      direction,
    };
  };

  const getConvsChargeCount = () => {
    if (isCountInfinity) return t("UNLIMITED");

    if (isNaN(convsChargeCount)) return "";

    return convsChargeCount;
  };

  const getPackageInfoText = () => {
    const packageType = container?.package?.type;
    const packageName = container?.package?.name;
    const remainingTime = container?.account?.time;

    const reaminingTimeText =
      packageType !== "expire" ? `: ${remainingTime} روز` : "";

    return `پکیج ${packageName}` + reaminingTimeText;
  };

  const getTelegramStatus = () => {
    return !!agent.isTelegramActive
      ? "enable"
      : !agent.telegramChatId
      ? "disable"
      : "waiting";
  };

  const renderTelegramStatus = (
    <span className="telegram_status" data-color={getTelegramStatus()}>
      {!!agent.isTelegramActive
        ? `${t("PROFILE").SETTINGS.ENABLED}`
        : !agent.telegramChatId
        ? `${t("PROFILE").SETTINGS.DISABLED}`
        : `${t("PROFILE").SETTINGS.PEND_FOR_CONFIRM}`}
    </span>
  );

  const profileInputs = [
    {
      title: t("PROFILE").SETTINGS.NAME,
      description: t("PROFILE").SETTINGS.NAME_DES,
      name: "name",
      inputType: "text",
      id: "profile_name",
      placeholder: t("PROFILE").SETTINGS.NAME_PH,
      disabled: false,
      validator: (value) => {
        return {
          isValid: value.length,
          msg: value.length ? "" : FIELD_REQUIRED,
        };
      },
      dir: "right",
    },
    {
      title: t("PROFILE").SETTINGS.AGENT_TITLE,
      description: t("PROFILE").SETTINGS.AGENT_TITLE_DES,
      name: "title",
      inputType: "text",
      id: "profile_title",
      placeholder: t("PROFILE").SETTINGS.AGENT_TITLE_PH,
      disabled: false,
      validator: (value) => {
        return {
          isValid: value.length,
          msg: value.length ? "" : FIELD_REQUIRED,
        };
      },
      dir: "right",
    },
    {
      title: t("PROFILE").SETTINGS.EMAIL,
      description: t("PROFILE").SETTINGS.EMAIL_DES,
      name: "email",
      inputType: "email",
      id: "profile_email",
      placeholder: "example@domain.com",
      disabled: false,
      readOnly: true,
      validator: (value) => {
        if (!value.length) {
          return {
            isValid: false,
            msg: FIELD_REQUIRED,
          };
        }

        if (!isValidEmail(value)) {
          return {
            isValid: false,
            msg: INVALID_EMAIL,
          };
        }

        return VALID_INPUT_OBJ;
      },
      dir: "left",
    },
    {
      title: t("PROFILE").SETTINGS.PASSWORD,
      description: t("PROFILE").SETTINGS.PASSWORD_DES,
      name: "password",
      inputType: "password",
      id: "profile_password",
      placeholder: t("PROFILE").SETTINGS.PASSWORD_PH,
      disabled: false,
      validator: (value) => {
        if (value.length > 0) {
          const isMatch = value.match(
            /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?!.* ).{6,}$/
          );
          if (!isMatch) {
            return {
              isValid: false,
              msg: INVALID_PASSWORD,
            };
          }
        }

        // leave it if the pass is empty
        return VALID_INPUT_OBJ;
      },
      dir: "right",
    },
    {
      title: t("PROFILE").SETTINGS.AVATAR,
      description: t("PROFILE").SETTINGS.AVATAR_DES,
      name: "avatar",
      inputType: "file",
      id: "profile_image",
      disabled: false,
      validator: () => VALID_INPUT_OBJ,
      dir: "initial",
    },

    // this feature is not implemented yet,
    // then aded an attribute named "disabled" for preventing showing then feature.
    {
      title: t("PROFILE").SETTINGS.THEME,
      description: t("PROFILE").SETTINGS.THEME_DES,
      name: "appStyle",
      inputType: "select",
      id: "profile_theme",
      options: [
        {
          title: t("PROFILE").SETTINGS.LIGHT_MODE,
          value: "1",
        },
        {
          title: t("PROFILE").SETTINGS.DARK_MODE,
          value: "2",
        },
      ],
      // TODO: remove this attribute after dark mode implemented.
      disabled: true,
      validator: () => VALID_INPUT_OBJ,
    },
  ];

  const isFormInValid = !profileInputs?.every((x) => {
    return x.validator(editingAgent ? editingAgent[x?.name] || "" : "").isValid;
  });

  const disableSubmitBtn = isLoadingSubmit || isFormInValid;

  const handleExitBtnClick = (e) => {
    e.stopPropagation();

    if (!!setShowLogoutModal) {
      setShowLogoutModal(true);
    }

    toggleSettings();
  };

  const renderSubmitBtnContent = () => {
    if (isLoadingSubmit) {
      return (
        <ThreeDots
          height="20"
          width="35"
          radius="9"
          color="#fff"
          ariaLabel="three-dots-loading"
          wrapperStyle={{ justifyContent: "center" }}
          visible={true}
        />
      );
    }

    return t("PROFILE").SETTINGS.SUBMIT;
  };

  const getAvatarImageUrl = () => {
    // when image is selected
    if (selectedImage?.url) {
      return selectedImage?.url;
    }

    // returned image url
    if (editingAgent.avatar) {
      return editingAgent.avatar;
    }

    return DEFAULT_AVATAR_URL;
  };

  // const handleSendNotificatios = () => {
  //   dispatch(updateNotificationSounds(formData));
  // };

  useEffect(() => {
    setEditingAgent(agent);
  }, [agent]);

  return (
    editingAgent && (
      <div className="settings-layout-container">
        <div className="settings-header">
          <div className="flex flex-ai-c settings-header-right">
            <ImageInputWrapper handleChange={handleChangeImageInput}>
              {/* <div className="settings__image_upload__text">
              {t("PROFILE").SETTINGS.BROWSE_IMAGE}
               </div> */}
              <div className="img_container">
                <img alt="agent_img" src={getAvatarImageUrl()} />
              </div>
            </ImageInputWrapper>

            <p className="agent-name">{editingAgent["name"]}</p>
          </div>

          <div className="flex flex-ai-c flex-columngap-16 settings-header-left">
            <div className="lang-settings">
              <SelectBox
                value={getAppLocale()}
                options={APP_LOCALES}
                name={"lang"}
                title={t("PROFILE").SETTINGS.LANG_SELECT_DEFAULT_OP}
                onChange={(e) => handleChangeLocale(e.target.value)}
              />
            </div>

            <div className="close-settings" onClick={handleCloseSettings}>
              {/* <img src={XIcon} alt="close icon" /> */}
              <XIcon />
            </div>
          </div>
        </div>

        {/* purchased package and conversations number */}
        <div className="settings__conv_count_package">
          <div className="settings__conv_count_package__inner">
            <div className="settings__conv_count_package__pack_charge__wrapper">
              <div className="settings__conv_count_package__package">
                <span>{getPackageInfoText()}</span>
              </div>
              <div className="settings__conv_count_package__convs_count">
                <span>شارژ مکالمه : {getConvsChargeCount()}</span>
              </div>
            </div>

            <div className="settings__conv_count_package__convs_charge">
              <a
                className="settings__conv_count_package__convs_charge_link"
                rel="noreferrer"
                target="_blank"
                href={`${RAYCHAT_DASHBOARD_URL}/payment/package-upgrading`}
              >
                <span>{t("PACKAGE_UPGRADE")}</span>
                <PurchaseIcon />
              </a>
            </div>
          </div>
        </div>

        <div className="telegram-settings">
          <div className="telegram-settings-header">
            <img
              src={TelegramMiniIcon}
              alt="Telegram Icon"
              className="telegram-mini-icon"
            />
            <div className="flex flex-ai-c flex-columngap-8">
              <span className="telegram-bot-text">
                {t("PROFILE").SETTINGS.TELEGRAM_BOT}
              </span>

              {renderTelegramStatus}
            </div>
          </div>
          <div className="telegram-settings-body">
            <div className="telegram-settings-desc">
              {t("PROFILE").SETTINGS.TELEGRAM_DES}
            </div>

            <div className="telegram-settings-status">
              {agent.isTelegramActive === true ? (
                <span
                  className="active__telegram"
                  onClick={() => dispatch(updateTelegramBot(false))}
                >
                  {t("PROFILE").SETTINGS.DISABLE}
                </span>
              ) : (
                !agent.isTelegramActive &&
                (!agent.telegramChatId ? (
                  <a
                    href={`https://t.me/Raychatnew_bot?start=${agent.id}`}
                    target="blank"
                    rel="noreferrer"
                    className="deactive_talagram"
                  >
                    {t("PROFILE").SETTINGS.ENABLE}
                  </a>
                ) : (
                  <span
                    className="confirm__telegram"
                    onClick={() => dispatch(updateTelegramBot(true))}
                  >
                    {t("PROFILE").SETTINGS.CONFIRM_ACTIVATION}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="personal-settings-wrapper">
          <TypicalHeader
            headerText={t("PROFILE").SETTINGS.TITLE}
            iconAlt={"UserInfoIcon"}
            iconSrc={UserInfoIcon}
          />
          <div className="personal-settings-container">
            {profileInputs.slice(0, 4)?.map((item, idx) => {
              return (
                <div
                  key={idx}
                  className="flex flex-ai-c flex-columngap-8 personal-setting-item"
                >
                  <span className="personal-setting-item-name">
                    {item.title}
                  </span>

                  <div className="flex flex-column flex-grow-1 personal-setting-item-input-container">
                    <input
                      value={editingAgent[item.name] || ""}
                      id={item.id}
                      type={item.inputType}
                      placeholder={item.placeholder}
                      name={item.name}
                      onChange={(e) =>
                        handleUpdateEditingAgent(e.target.name, e.target.value)
                      }
                      readOnly={!!item?.readOnly}
                      className={clsx([
                        !item.validator(editingAgent[item.name] || "").isValid
                          ? "has-error"
                          : undefined,
                        "personal-setting-item-input",
                      ])}
                      // TODO: refactor inline styling
                      style={getInputStyle(item.dir)}
                    />
                    {/* validator message */}
                    <span className="validation_text">
                      {item.validator(editingAgent[item.name] || "").msg}
                    </span>
                  </div>
                </div>
              );
            })}

            <div className="flex flex-ai-c flex-jc-sb flex-columngap-8 personal-setting-item">
              <span className="personal-setting-item-name">تم وب اپ</span>

              <div className="flex flex-ai-c flex-jc-e flex-grow-1 personal-setting-item-input-container">
                <ThemeSettings onThemeToggle={handleThemeToggle} />
              </div>
            </div>
          </div>
        </div>

        <div className="alarms-settings-wrapper">
          <Notifications formData={formData} setFormData={setFormData} />
        </div>

        <div className="settings-actions">
          <button
            onClick={handleSendAgentInfo}
            className="settings_btn"
            data-disabled={disableSubmitBtn}
            disabled={disableSubmitBtn}
          >
            {renderSubmitBtnContent()}
          </button>

          <button
            className="settings_list_item settings_list_item_exit"
            onClick={handleExitBtnClick}
          >
            <img
              src={PowerButton}
              alt="Power Button"
              className="power-button"
            />
            {t("PROFILE").SETTINGS.EXIT}
          </button>
        </div>
      </div>
    )
  );
};

export default Profile;
